'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { ArticlesCountIcon } from '@/components/icons';
import { Breadcrumb } from '@/components/Breadcrumb/breadcrumb';
import { ListingStructuredData } from '@/features/seo-listing/components/listing-structured-data';
import { CatalogPageNav } from '@/features/seo-listing/components/catalog-page-nav';
import { SeoListingHeader } from '@/features/seo-listing/components/seo-listing-header';
import { useDebounce } from '@/hooks/use-debounce';
import { api } from '@/lib/api';
import { getCharacterInitials } from '@/features/seo-listing/utils/get-character-initials.util';
import {
  buildFandomCharactersHref,
  buildFandomPermalinkHref,
  buildListingBreadcrumbs,
  formatListingCount,
  resolveCatalogEntityHref,
  resolveListingHeading,
} from '@/features/seo-listing/utils/seo-listing';
import { createCatalogNavigateHandler } from '@/features/fanfic-card/consts/LISTING_FANDOM_NAVIGATE_EVENT';

const CATALOG_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

export type FandomCharacterCard = {
  id: string;
  name: string;
  count: number;
  description?: string | null;
  slug?: string | null;
  permanentUrl?: string | null;
  imageUrl?: string | null;
};

type FandomCharactersHubProps = {
  fandomSlug: string;
  fandomId: string;
  fandomTitle: string;
  characters: FandomCharacterCard[];
  charactersCount: number;
  page: number;
  totalPages: number;
  searchQuery: string;
};

type CharactersCustomItem = {
  id: string | number;
  name?: string;
  description?: string | null;
  slug?: string | null;
  permanentUrl?: string | null;
  fanfic_cnt?: number;
  image?: { id?: number; url?: string | null } | null;
};

type CharactersCustomResponse = {
  characters?: CharactersCustomItem[];
  total?: number;
  totalDocs?: number;
  totalPages?: number;
};

const mapCharacters = (items: CharactersCustomItem[] | undefined): FandomCharacterCard[] =>
  (items ?? []).map((character) => ({
    id: String(character.id),
    name: character.name || '',
    count: character.fanfic_cnt || 0,
    description: character.description,
    slug: character.slug,
    permanentUrl: character.permanentUrl,
    imageUrl: character.image?.url ?? null,
  }));

const buildCharactersHubPath = (fandomSlug: string, search: string, pageNumber: number): string =>
  buildFandomCharactersHref(fandomSlug, { search, page: pageNumber });

const syncCharactersHubUrl = (href: string): void => {
  if (typeof window === 'undefined') return;
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === href) return;
  window.history.replaceState(window.history.state, '', href);
};

export function FandomCharactersHub({
  fandomSlug,
  fandomId,
  fandomTitle,
  characters: initialCharacters,
  charactersCount: initialCharactersCount,
  page: initialPage,
  totalPages: initialTotalPages,
  searchQuery: initialSearchQuery,
}: FandomCharactersHubProps) {
  const [query, setQuery] = useState(initialSearchQuery);
  const [syncedSearchQuery, setSyncedSearchQuery] = useState(initialSearchQuery);
  const [characters, setCharacters] = useState(initialCharacters);
  const [charactersCount, setCharactersCount] = useState(initialCharactersCount);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [hasUserEditedSearch, setHasUserEditedSearch] = useState(false);
  const requestSeqRef = useRef(0);

  if (initialSearchQuery !== syncedSearchQuery) {
    setSyncedSearchQuery(initialSearchQuery);
    setQuery(initialSearchQuery);
    setCharacters(initialCharacters);
    setCharactersCount(initialCharactersCount);
    setPage(initialPage);
    setTotalPages(initialTotalPages);
    setHasUserEditedSearch(false);
  }

  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const fandomHref = buildFandomPermalinkHref(fandomSlug);
  const breadcrumbs = buildListingBreadcrumbs({
    kind: 'fandom_characters',
    fandomTitle,
    fandomHref,
  });
  const heading = resolveListingHeading({ kind: 'fandom_characters', fandomTitle });
  const currentPath = buildCharactersHubPath(fandomSlug, debouncedQuery, page);

  useEffect(() => {
    const href = buildCharactersHubPath(fandomSlug, debouncedQuery, page);
    syncCharactersHubUrl(href);

    const matchesSsrSnapshot =
      !hasUserEditedSearch &&
      debouncedQuery.trim() === initialSearchQuery.trim() &&
      page === initialPage;
    if (matchesSsrSnapshot) {
      return;
    }

    const requestSeq = ++requestSeqRef.current;
    const controller = new AbortController();

    const params = new URLSearchParams();
    params.set('fandomId', fandomId);
    params.set('page', String(page));
    params.set('limit', String(CATALOG_PAGE_SIZE));
    params.set('minFanfics', '1');
    if (debouncedQuery.trim()) params.set('search', debouncedQuery.trim());

    void api
      .get<CharactersCustomResponse>(`/api/characters-custom?${params.toString()}`, {
        signal: controller.signal,
      })
      .then((response) => {
        if (requestSeq !== requestSeqRef.current) return;
        setCharacters(mapCharacters(response.data?.characters));
        setCharactersCount(response.data?.total ?? response.data?.totalDocs ?? 0);
        setTotalPages(response.data?.totalPages ?? 1);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (requestSeq !== requestSeqRef.current) return;
        console.error('[FandomCharactersHub] Failed to search characters:', error);
      });

    return () => {
      controller.abort();
    };
  }, [
    debouncedQuery,
    fandomId,
    fandomSlug,
    hasUserEditedSearch,
    initialPage,
    initialSearchQuery,
    page,
  ]);

  return (
    <div className="container mx-auto px-3 xs:px-4 py-6">
      <div className="mb-6">
        <ListingStructuredData
          breadcrumbs={breadcrumbs}
          heading={heading}
          currentPath={currentPath}
          works={[]}
        />
        <div className="mb-3">
          <Breadcrumb
            items={breadcrumbs}
            variant="light"
            onItemNavigate={(href, event) => {
              const path = href.split('?')[0] ?? href;
              if (path === '/fanfics' || path === '/originals') {
                createCatalogNavigateHandler(path)(event);
              }
            }}
          />
        </div>
        <SeoListingHeader
          h1={heading}
          worksCount={null}
          charactersCount={charactersCount}
          charactersCountSuffix="фэндома"
        />
      </div>
      <div className="mb-6">
        <input
          type="search"
          value={query}
          onChange={(event) => {
            setHasUserEditedSearch(true);
            setQuery(event.target.value);
            setPage(1);
          }}
          placeholder="Имя или прозвище — Помни, Джекс, Кейн..."
          className="h-12 w-full rounded-[10px] border-none bg-white px-4 text-sm outline-none"
        />
      </div>
      {characters.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">По вашему запросу ничего не найдено</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 items-stretch gap-3 min-[580px]:grid-cols-3 min-[1160px]:grid-cols-4 xl:grid-cols-5">
          {characters.map((character) => {
            const href =
              resolveCatalogEntityHref({
                fandomSlug,
                fandomId,
                characterSlug: character.slug,
                characterId: character.id,
                permanentUrl: character.permanentUrl,
              }) ?? fandomHref;
            return (
              <Link
                key={character.id}
                href={href}
                className="group flex h-full flex-col overflow-hidden rounded-[16px] bg-white shadow-sm"
              >
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#E3E5ED]">
                  {character.imageUrl ? (
                    <Image
                      src={character.imageUrl}
                      alt={character.name}
                      fill
                      className="object-cover"
                      sizes="(max-width: 580px) 50vw, (max-width: 1160px) 33vw, 210px"
                    />
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center text-3xl font-black uppercase text-[#1B1D22]/35">
                      {getCharacterInitials(character.name)}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-3">
                  <p className="truncate text-base font-black uppercase leading-6 text-[#1B1D22] transition-colors group-hover:text-[#585cff]">
                    {character.name}
                  </p>
                  {character.description ? (
                    <p className="mt-1 line-clamp-2 text-xs font-medium leading-4 text-[#6E7384]">
                      {character.description}
                    </p>
                  ) : null}
                  <div className="mt-auto flex shrink-0 items-center gap-3 pt-3 text-xs font-medium text-[#6E7384]">
                    <span className="inline-flex items-center gap-1">
                      <ArticlesCountIcon
                        className="h-3.5 w-3.5 shrink-0 text-[#B4BACD]"
                        aria-hidden
                      />
                      {formatListingCount(character.count)}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      <CatalogPageNav
        page={page}
        totalPages={totalPages}
        onPageChange={(nextPage) => {
          setHasUserEditedSearch(true);
          setPage(nextPage);
        }}
      />
    </div>
  );
}
